import dayjs from 'dayjs'
import utc from 'dayjs/plugin/utc'
import {
  AssetCellMetaEntry,
  AssetColumn,
  AssetColumnKind,
  AssetRecordWire,
  AssetRow,
  AssetSource,
  AssetSourceWire
} from 'models'
import { EM_DASH, sanitizeAssetValue } from 'lib/sanitizeAssetValue'
import { toSource } from './assetEntity'
import { toProductSource } from './assetSources'

dayjs.extend(utc)

const DATE_DISPLAY = 'MMM DD, YYYY'
/* The pane lists every reported value, and two on the same day would otherwise read as duplicates. */
export const DATE_TIME_DISPLAY = 'MMM DD, YYYY HH:mm'

/* Naive timestamps carry no zone, so they are formatted as written rather than shifted. */
export const formatCellValue = (value: string | null, kind: AssetColumnKind, dateFormat = DATE_DISPLAY): string => {
  if (!value) return EM_DASH
  if (kind !== AssetColumnKind.Timestamp) return value

  const parsed = dayjs(value)

  return parsed.isValid() ? parsed.format(dateFormat) : value
}

/**
 * `last_updated` is when the build ran; it reads only batches synced before its own UTC day, so the
 * data is as of the day before. `snapshot_date` is not used — it lags when matching fails.
 */
export const formatDataAsOf = (lastUpdatedAt: string | null): string => {
  const built = lastUpdatedAt ? dayjs.utc(lastUpdatedAt) : null

  return built?.isValid() ? built.subtract(1, 'day').format('ddd, MMM DD, YYYY') : EM_DASH
}

/* An array cell prints one element: `CELL_META.value` names it, else the raw array's first. */
const cellText = (raw: unknown, meta: AssetCellMetaEntry | undefined, kind: AssetColumnKind): string | null => {
  if (kind !== AssetColumnKind.Array) return sanitizeAssetValue(raw)

  if (meta?.value !== undefined) return sanitizeAssetValue(meta.value)

  return sanitizeAssetValue(Array.isArray(raw) ? raw[0] : raw)
}

/* The cell draws a logo per instance; a slug from an older build is an instance of its own. */
const toRowSource = (source: string | AssetSourceWire): AssetSource | null => {
  if (typeof source === 'string') {
    const slug = sanitizeAssetValue(source)

    return slug ? toProductSource(slug) : null
  }

  if (!source?.product_name && !source?.instance_name) return null

  /* The slug picks the logo, so an unnamed product falls back to the instance that embeds it. */
  return toSource({ ...source, product_name: source.product_name || source.instance_name })
}

/**
 * A cell prints one value, so `+N` counts the ones it left out: the rest of an array column, or
 * the values the merged records disagreed on. The larger of the two, never the sum — the merge can
 * settle on an element from another member, which both counts would then claim.
 */
const hiddenCount = (
  raw: unknown,
  meta: AssetCellMetaEntry | undefined,
  shown: string | null,
  kind: AssetColumnKind
): number => {
  const reported = meta?.more ?? 0

  if (kind !== AssetColumnKind.Array || !Array.isArray(raw)) return reported

  const distinct = new Set(raw.map(sanitizeAssetValue).filter((value): value is string => !!value))

  if (shown) distinct.add(shown)

  return Math.max(distinct.size - 1, reported)
}

export const toAssetRow = (record: AssetRecordWire, columns: AssetColumn[]): AssetRow => {
  const meta = record.CELL_META ?? {}
  const values: Record<string, string | null> = {}
  const badges: Record<string, number> = {}

  for (const column of columns) {
    const text = cellText(record[column.name], meta[column.name], column.kind)

    values[column.name] = text

    const more = hiddenCount(record[column.name], meta[column.name], text, column.kind)

    if (more) badges[column.name] = more
  }

  return {
    id: record.ENTITY_ID,
    sources: (record.SOURCES ?? []).map(toRowSource).filter((source): source is AssetSource => !!source),
    memberCount: record.MEMBER_COUNT ?? 1,
    values,
    badges
  }
}
